import Alert from "@cloudscape-design/components/alert"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import ColumnLayout from "@cloudscape-design/components/column-layout"
import Container from "@cloudscape-design/components/container"
import ContentLayout from "@cloudscape-design/components/content-layout"
import FormField from "@cloudscape-design/components/form-field"
import Header from "@cloudscape-design/components/header"
import Input from "@cloudscape-design/components/input"
import Modal from "@cloudscape-design/components/modal"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Spinner from "@cloudscape-design/components/spinner"
import { zodResolver } from "@hookform/resolvers/zod"
import { useState, useEffect, Fragment } from "react"
import { Controller, useForm } from "react-hook-form"
import { useParams, useNavigate } from "react-router-dom"
import { useProtocolDetail, useSaveProtocol } from "@/hooks/queries/useProtocol"
import AssessmentsGrid from "./AssessmentsGrid"
import { DEFAULT_FORM_VALUES, generateVisitId, partSectionLabel } from "./constants"
import { protocolFormSchema, ProtocolFormData } from "./schema"

/**
 * Form for creating and editing study protocols.
 *
 * Handles loading existing protocol data, form validation via Zod schema, and submission to the
 * API. Includes the AssessmentsGrid for managing the schedule of activities (visits and
 * assessments). Changes stay local until saved; leaving the page with unsaved changes prompts a
 * save/discard confirmation.
 */
const ProtocolForm = () => {
  const { studyId } = useParams<{ studyId: string }>()
  const navigate = useNavigate()

  const { data: existingProtocol, isLoading: loading } = useProtocolDetail(studyId)
  const saveProtocol = useSaveProtocol(studyId ?? "")
  const saving = saveProtocol.isPending
  const [error, setError] = useState<string | null>(null)
  const [isNew, setIsNew] = useState(true)
  // Path the user tried to leave to while the form was dirty; non-null shows the leave guard.
  const [pendingLeavePath, setPendingLeavePath] = useState<string | null>(null)

  const form = useForm<ProtocolFormData>({
    defaultValues: DEFAULT_FORM_VALUES,
    resolver: zodResolver(protocolFormSchema),
  })

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = form

  const formValues = watch()

  // Seed the form from the fetched protocol (data comes from useProtocolDetail).
  useEffect(() => {
    const protocol = existingProtocol
    if (protocol?.data) {
      setIsNew(false)
      const parts = (protocol.data.parts ?? []).map((part) => ({
        name: part.name || "",
        periods: part.periods.map((period) => ({
          ...period,
          visits: period.visits.map((visit) => ({
            ...visit,
            id: visit.id || generateVisitId(),
            visit_number: String(visit.visit_number ?? ""),
          })),
        })),
        assessments: Array.isArray(part.assessments) ? part.assessments : [],
      }))
      reset({
        study_title: protocol.data.study_title || "",
        sponsor: protocol.data.sponsor || "",
        phase: protocol.data.phase || "",
        parts: parts.length ? parts : DEFAULT_FORM_VALUES.parts,
      })
    }
  }, [existingProtocol, reset])

  // Warn on tab close / hard reload with unsaved changes
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirty) e.preventDefault()
    }
    window.addEventListener("beforeunload", handler)
    return () => window.removeEventListener("beforeunload", handler)
  }, [isDirty])

  // In-app exits go through here so dirty state can intercept with the leave guard
  const guardedNavigate = (path: string) => {
    if (isDirty) {
      setPendingLeavePath(path)
    } else {
      navigate(path)
    }
  }

  // Form submission handler
  const onSubmit = async (data: ProtocolFormData) => {
    if (!studyId) return
    setError(null)
    try {
      await saveProtocol.mutateAsync({ data, isNew })
      navigate(`/studies/${studyId}?tab=protocol`)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save protocol")
      setPendingLeavePath(null)
    }
  }

  const saveLabel = isNew ? "Create Protocol" : "Save & Approve"

  if (loading) {
    return (
      <Box textAlign="center" padding="xxl">
        <Spinner size="large" />
        <Box variant="p" color="text-body-secondary">
          Loading...
        </Box>
      </Box>
    )
  }

  return (
    <ContentLayout
      header={
        <SpaceBetween size="xs">
          <Button
            variant="link"
            iconName="arrow-left"
            onClick={() => guardedNavigate(`/studies/${studyId}?tab=protocol`)}
          >
            Back to Study
          </Button>
          <Header
            variant="h1"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  variant="link"
                  onClick={() => guardedNavigate(`/studies/${studyId}?tab=protocol`)}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  loading={saving}
                  onClick={() => void handleSubmit(onSubmit)()}
                >
                  {saveLabel}
                </Button>
              </SpaceBetween>
            }
          >
            {isNew ? "Create Protocol" : "Edit Protocol"}
          </Header>
        </SpaceBetween>
      }
    >
      <form onSubmit={(e) => void handleSubmit(onSubmit)(e)}>
        <SpaceBetween size="l">
          {error && <Alert type="error">{error}</Alert>}

          {Object.keys(errors).length > 0 && (
            <Alert type="error" header="Please fix the following errors:">
              <ul>
                {errors.parts?.map?.((partError, pIdx) => (
                  <Fragment key={`part-${pIdx}`}>
                    {partError?.periods?.map?.((periodError, perIdx) => {
                      const periodName =
                        formValues.parts?.[pIdx]?.periods?.[perIdx]?.name || `Period ${perIdx + 1}`
                      return (
                        <Fragment key={`period-${pIdx}-${perIdx}`}>
                          {periodError?.name && (
                            <li>
                              {periodName}: {periodError.name.message}
                            </li>
                          )}
                          {periodError?.visits?.map?.(
                            (visitError, vIdx) =>
                              visitError?.visit_number && (
                                <li key={`visit-${pIdx}-${perIdx}-${vIdx}`}>
                                  {periodName}, Visit {vIdx + 1}: {visitError.visit_number.message}
                                </li>
                              ),
                          )}
                        </Fragment>
                      )
                    })}

                    {partError?.assessments?.map?.((assessmentError, aIdx) => {
                      const assessmentName =
                        formValues.parts?.[pIdx]?.assessments?.[aIdx]?.name ||
                        `Assessment ${aIdx + 1}`
                      return (
                        assessmentError?.name && (
                          <li key={`assessment-${pIdx}-${aIdx}`}>
                            {assessmentName}: {assessmentError.name.message}
                          </li>
                        )
                      )
                    })}
                  </Fragment>
                ))}
              </ul>
            </Alert>
          )}

          <Container header={<Header variant="h2">Protocol Information</Header>}>
            <ColumnLayout columns={3}>
              <Controller
                name="study_title"
                control={control}
                render={({ field }) => (
                  <FormField label="Study Title">
                    <Input
                      value={field.value ?? ""}
                      onChange={({ detail }) => field.onChange(detail.value)}
                      onBlur={field.onBlur}
                      placeholder="Enter study title"
                    />
                  </FormField>
                )}
              />
              <Controller
                name="sponsor"
                control={control}
                render={({ field }) => (
                  <FormField label="Sponsor">
                    <Input
                      value={field.value ?? ""}
                      onChange={({ detail }) => field.onChange(detail.value)}
                      onBlur={field.onBlur}
                      placeholder="Enter sponsor name"
                    />
                  </FormField>
                )}
              />
              <Controller
                name="phase"
                control={control}
                render={({ field }) => (
                  <FormField label="Phase">
                    <Input
                      value={field.value ?? ""}
                      onChange={({ detail }) => field.onChange(detail.value)}
                      onBlur={field.onBlur}
                      placeholder="e.g., Phase 2b/3"
                    />
                  </FormField>
                )}
              />
            </ColumnLayout>
          </Container>

          {(formValues.parts?.length ? formValues.parts : [null]).map((part, index, all) => (
            <AssessmentsGrid
              key={index}
              form={form}
              partIndex={index}
              partLabel={partSectionLabel(part?.name, index, all.length)}
            />
          ))}
        </SpaceBetween>
      </form>

      {/* Leave guard: shown when navigating away with unsaved changes */}
      {pendingLeavePath !== null && (
        <Modal
          visible
          onDismiss={() => setPendingLeavePath(null)}
          header="Unsaved changes"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => navigate(pendingLeavePath)} disabled={saving}>
                  Discard changes
                </Button>
                <Button
                  variant="primary"
                  loading={saving}
                  onClick={() => void handleSubmit(onSubmit, () => setPendingLeavePath(null))()}
                >
                  {saveLabel}
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          You have unsaved changes to this protocol. Save them before leaving, or discard them.
        </Modal>
      )}
    </ContentLayout>
  )
}

export default ProtocolForm
